/**
 * Tiny DOM helpers + shared selectors for ESM modules.
 *
 * `fireChange(el)` — dispatch a bubbling `change` event. Used to drive the
 * collection-filters change delegate from synthetic state updates (size
 * reset, radio toggle-off, remove-select-filter button) so a single
 * handler chain owns URL building.
 *
 * `unwrapJQuery(maybe)` — return the underlying DOM element of a jQuery
 * collection, or pass through a native Element / null / undefined
 * unchanged. The naive `x && x[0] ? x[0] : x` pattern that this replaces
 * has a CRITICAL bug for `<form>` callers: `HTMLFormElement` supports
 * indexed access to its form controls (`form[0]` is the first input),
 * so the truthy check unwraps a real form into its first input, and
 * downstream `new FormData(input)` throws "parameter 1 is not of type
 * 'HTMLFormElement'". Test for the jQuery-specific `.jquery` version-
 * string property — only jQuery collections have it.
 *
 * `COLLECTION_SELECTOR` — the `#collection` container selector shared by
 * the four collection ESM files. Centralizing keeps a future rename
 * one-line, not four.
 *
 * Source path: theme/js/core/dom.esm.js
 * Built name : ./dom.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 *
 * [Dom]
 */

export function fireChange(el) {
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

export function unwrapJQuery(maybeJQ) {
  if (maybeJQ && typeof maybeJQ.jquery === 'string') return maybeJQ[0];
  return maybeJQ;
}

// Parse a fetched HTML string and return the first matching element, or
// null if not found. Centralizes the `new DOMParser().parseFromString(...)
// .querySelector(...)` triplet shared by section-rendering custom
// elements (<cart-items>, <cart-icon-bubble>) and ajax-router fragments.
export function parseFragment(html, selector) {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector(selector);
}

// Phase 13: app.trapFocus is now native-DOM internally (accepts an
// Element directly). The jQuery wrap is gone.
export function trapFocusOn(el) {
  if (!el) return;
  const a = window.app;
  if (!a || typeof a.trapFocus !== 'function') return;
  a.trapFocus(el);
}

export const COLLECTION_SELECTOR = '#collection';
